/**
 * Shared day/night lighting calculations.
 *
 * All systems (SunLighting, Clouds, Ocean, Terrain) must use the same
 * edge constants so transitions are perfectly synchronised.
 *
 * Sun height `h` is the normalised y-component of the sun direction
 * vector, roughly in the range [-0.43, +0.43].
 */

// ---------------------------------------------------------------------------
// Edge constants (shared with GLSL — keep the GLSL #defines in sync!)
// ---------------------------------------------------------------------------

/** Day factor ramps from 0 to 1 across this range of sun height. */
export const DAY_EDGE0 = -0.1;
export const DAY_EDGE1 = 0.25;

/** Sunset band lower/upper bounds. */
export const SUNSET_EDGE0 = -0.05;
export const SUNSET_EDGE1 = 0.1;
export const SUNSET_EDGE2 = 0.1;
export const SUNSET_EDGE3 = 0.3;

/** Sun intensity (used for lerping colors). */
export const SUN_INTENSITY_EDGE0 = -0.05;
export const SUN_INTENSITY_EDGE1 = 0.3;

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

function smoothstep(x: number, edge0: number, edge1: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// ---------------------------------------------------------------------------
// Shared factor calculations
// ---------------------------------------------------------------------------

/** 0 = full night, 1 = full day */
export function calculateDayFactor(h: number): number {
  return smoothstep(h, DAY_EDGE0, DAY_EDGE1);
}

/** Peaks around horizon, 0 when sun is high or well below. */
export function calculateSunsetFactor(h: number): number {
  return (
    smoothstep(h, SUNSET_EDGE0, SUNSET_EDGE1) *
    (1 - smoothstep(h, SUNSET_EDGE2, SUNSET_EDGE3))
  );
}

/** 0-1, used to scale colour/intensity of sun-dependent effects. */
export function calculateSunIntensity(h: number): number {
  return smoothstep(h, SUN_INTENSITY_EDGE0, SUN_INTENSITY_EDGE1);
}

// ---------------------------------------------------------------------------
// Shared colour calculations
// ---------------------------------------------------------------------------

/** Directional light sun colour (warm at sunset, fades at night). */
export function calculateSunColor(h: number): [number, number, number] {
  const day = calculateDayFactor(h);
  const sunset = calculateSunsetFactor(h);
  return [
    1.0 * day,
    (1.0 - sunset * 0.3) * day,
    (1.0 - sunset * 0.5) * day,
  ];
}

/** Ambient colour — very low, slightly cool-blue, does NOT change with sun. */
export function calculateAmbientColor(): [number, number, number] {
  return [0.06, 0.06, 0.10];
}

/** Ambient intensity — very low and constant. */
export function calculateAmbientIntensity(): number {
  return 1.0; // colour already encodes the intensity
}

// ---------------------------------------------------------------------------
// GLSL snippet — paste into shaders that need day/sunset/sunIntensity
// These must mirror the JS constants exactly.
// ---------------------------------------------------------------------------

/**
 * Returns a GLSL code block that declares the standardised day/night
 * helper functions. Insert at the top of fragment shaders.
 */
export const GLSL_LIGHTING_HEADER = /* glsl */ `
  // --- Shared day/night constants (must match utils/lighting.ts) ---
  #define DAY_EDGE0      -0.1
  #define DAY_EDGE1       0.25
  #define SUNSET_EDGE0   -0.05
  #define SUNSET_EDGE1    0.1
  #define SUNSET_EDGE2    0.1
  #define SUNSET_EDGE3    0.3
  #define SUN_INT_EDGE0  -0.05
  #define SUN_INT_EDGE1   0.3

  float dayFactor(float h) {
    return smoothstep(DAY_EDGE0, DAY_EDGE1, h);
  }
  float sunsetFactor(float h) {
    return smoothstep(SUNSET_EDGE0, SUNSET_EDGE1, h)
         * (1.0 - smoothstep(SUNSET_EDGE2, SUNSET_EDGE3, h));
  }
  float sunIntensityFactor(float h) {
    return smoothstep(SUN_INT_EDGE0, SUN_INT_EDGE1, h);
  }
`;

export { smoothstep, lerp };
